import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import MUIProvider from '~/contexts/MUIProvider';

import { Alert, Box, Container, LinearProgress, Paper, Stack, Typography } from '@mui/material';

import { ROLE_HERO_PLANNER_ADMIN } from '~/consts/roles';
import tenants from '~/consts/tenant';

import {
  useCreateTranslationCatalog,
  useDeleteTranslationCatalog,
  useDeleteTranslationCatalogField,
  useLockTranslationCatalog,
  usePatchTranslationCatalog,
  useTranslateTranslationCatalog,
  useTranslationCatalog,
  useTranslationCatalogs,
  useUnlockTranslationCatalog,
  useUploadTranslationCatalog,
} from '~/hooks/apis/customer-communication/useTranslationCatalog';
import useCurrentUser from '~/hooks/useCurrentUser';

import { downloadCsvFile } from '~/utils/csvDownload';

import NewCatalogDialog from './NewCatalogDialog';
import TranslationCatalogEditor, { mapEditorRowsToPatch } from './TranslationCatalogEditor';
import TranslationCatalogList from './TranslationCatalogList';
import TranslationCatalogToolbar from './TranslationCatalogToolbar';
import UnlockDialog from './UnlockDialog';
import {
  buildTranslationCatalogCsvRows,
  getTranslationCatalogCsvHeaders,
  isTranslationCatalogLocked,
  parseTranslationCatalogCsv,
} from './translationCatalogUtils';
import type { TranslationCatalogCreatePayload, TranslationCatalogRow } from './types';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  return String(error);
}

function getCsvFilename(catalogName?: string): string {
  const safeCatalogName = (catalogName || 'translation_catalog').replace(/[^A-Za-z0-9_-]+/g, '_');
  return `${safeCatalogName}.csv`;
}

function getCatalogNameFromCsvFilename(fileName: string): string {
  const filenameWithoutExtension = fileName.replace(/\.[^/.]+$/, '');
  return filenameWithoutExtension.replace(/[^A-Za-z0-9_-]+/g, '_') || 'translation_catalog';
}

function TranslationCatalogPageContent() {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useCurrentUser();
  const canMutate = !!user?.roles.includes(ROLE_HERO_PLANNER_ADMIN);
  const catalogsQuery = useTranslationCatalogs();
  const [selectedCatalogId, setSelectedCatalogId] = useState<string>();
  const [editorRows, setEditorRows] = useState<Array<TranslationCatalogRow>>([]);
  const [editorLocales, setEditorLocales] = useState<Array<string>>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isUnlockDialogOpen, setIsUnlockDialogOpen] = useState(false);
  const [isNewDialogOpen, setIsNewDialogOpen] = useState(false);
  const [deletingFieldName, setDeletingFieldName] = useState<string>();
  const [deletedCatalogId, setDeletedCatalogId] = useState<string>();
  const [importedDraft, setImportedDraft] = useState<{
    rows: Array<TranslationCatalogRow>;
    locales: Array<string>;
    hasDescriptionColumn: boolean;
    hasSourceValueColumn: boolean;
    nonce: number;
  }>();

  useEffect(() => {
    const firstCatalogId = catalogsQuery.data?.[0]?.id;

    if (!selectedCatalogId && firstCatalogId && firstCatalogId !== deletedCatalogId) {
      setSelectedCatalogId(firstCatalogId);
    }

    if (
      deletedCatalogId &&
      catalogsQuery.data &&
      !catalogsQuery.data.some((catalog) => catalog.id === deletedCatalogId)
    ) {
      setDeletedCatalogId(undefined);
    }
  }, [catalogsQuery.data, deletedCatalogId, selectedCatalogId]);

  const catalogQuery = useTranslationCatalog(selectedCatalogId);
  const createMutation = useCreateTranslationCatalog();
  const patchMutation = usePatchTranslationCatalog(selectedCatalogId);
  const deleteMutation = useDeleteTranslationCatalog(selectedCatalogId);
  const deleteFieldMutation = useDeleteTranslationCatalogField(selectedCatalogId);
  const translateMutation = useTranslateTranslationCatalog(selectedCatalogId);
  const lockMutation = useLockTranslationCatalog(selectedCatalogId);
  const unlockMutation = useUnlockTranslationCatalog(selectedCatalogId);
  const uploadMutation = useUploadTranslationCatalog(selectedCatalogId);
  const selectedCatalog = catalogQuery.data;
  const isLocked = isTranslationCatalogLocked(selectedCatalog);
  const isDeletingCatalog = deleteMutation.isPending;
  const isDeletingField = deleteFieldMutation.isPending;
  const isSubmitting =
    patchMutation.isPending ||
    deleteMutation.isPending ||
    deleteFieldMutation.isPending ||
    translateMutation.isPending ||
    lockMutation.isPending ||
    unlockMutation.isPending ||
    uploadMutation.isPending;
  const isCreating = createMutation.isPending;

  const handleRowsChange = useCallback(
    (rows: Array<TranslationCatalogRow>, locales: Array<string>, nextIsDirty: boolean) => {
      setEditorRows(rows);
      setEditorLocales(locales);
      setIsDirty(nextIsDirty);
    },
    [],
  );

  const handleMutationError = useCallback(
    (action: string, error: unknown) => {
      enqueueSnackbar(`${action} failed: ${getErrorMessage(error)}`, { variant: 'error' });
    },
    [enqueueSnackbar],
  );

  const handleCreate = useCallback(
    (payload: TranslationCatalogCreatePayload) => {
      createMutation.mutate(payload, {
        onSuccess: (catalog) => {
          setIsNewDialogOpen(false);
          setSelectedCatalogId(catalog.id);
          setIsDirty(false);
          enqueueSnackbar('Translation catalog created', { variant: 'success' });
        },
        onError: (error) => handleMutationError('Create', error),
      });
    },
    [createMutation, enqueueSnackbar, handleMutationError],
  );

  const handleCreateFromCsv = useCallback(
    async (file: File) => {
      try {
        const csvText = await file.text();
        const parsedCsv = parseTranslationCatalogCsv(csvText);
        const catalogPatch = mapEditorRowsToPatch(parsedCsv.rows, parsedCsv.locales);
        const catalogName = getCatalogNameFromCsvFilename(file.name);

        createMutation.mutate(
          {
            brand: selectedCatalog?.brand ?? tenants.lux.brand,
            catalogName,
            columnNames: catalogPatch.columnNames ?? [],
            descriptions: catalogPatch.descriptions,
            sourceValues: catalogPatch.sourceValues,
            translatedValues: catalogPatch.translatedValues,
          },
          {
            onSuccess: (catalog) => {
              setSelectedCatalogId(catalog.id);
              setImportedDraft(undefined);
              setIsDirty(false);
              enqueueSnackbar(`Created ${catalog.catalogName} from CSV`, { variant: 'success' });
              if (parsedCsv.rejectedHeaders.length > 0) {
                enqueueSnackbar(`Ignored unsupported CSV columns: ${parsedCsv.rejectedHeaders.join(', ')}`, {
                  variant: 'warning',
                });
              }
            },
            onError: (error) => handleMutationError('Create from CSV', error),
          },
        );
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
      }
    },
    [createMutation, enqueueSnackbar, handleMutationError, selectedCatalog?.brand],
  );

  const handleSave = useCallback(() => {
    patchMutation.mutate(mapEditorRowsToPatch(editorRows, editorLocales), {
      onSuccess: () => {
        setIsDirty(false);
        setImportedDraft(undefined);
        enqueueSnackbar('Translation catalog saved', { variant: 'success' });
      },
      onError: (error) => handleMutationError('Save', error),
    });
  }, [editorLocales, editorRows, enqueueSnackbar, handleMutationError, patchMutation]);

  const handleDeleteCatalog = useCallback(() => {
    if (!selectedCatalog) return;
    const confirmed = window.confirm(
      `Delete ${selectedCatalog.catalogName}? This removes the admin catalog and deletes the Braze catalog.`,
    );
    if (!confirmed) return;

    deleteMutation.mutate(undefined, {
      onSuccess: () => {
        const nextCatalog = catalogsQuery.data?.find((catalog) => catalog.id !== selectedCatalog.id);
        setDeletedCatalogId(selectedCatalog.id);
        setSelectedCatalogId(nextCatalog?.id);
        setEditorRows([]);
        setEditorLocales([]);
        setIsDirty(false);
        enqueueSnackbar('Translation catalog deleted', { variant: 'success' });
      },
      onError: (error) => handleMutationError('Delete catalog', error),
    });
  }, [catalogsQuery.data, deleteMutation, enqueueSnackbar, handleMutationError, selectedCatalog]);

  const handleDeleteField = useCallback(
    (fieldName: string) => {
      if (!selectedCatalog) return;
      const confirmed = window.confirm(
        `Delete ${fieldName} from ${selectedCatalog.catalogName}? This also deletes the field in Braze.`,
      );
      if (!confirmed) return;

      setDeletingFieldName(fieldName);
      deleteFieldMutation.mutate(fieldName, {
        onSuccess: () => {
          setIsDirty(false);
          enqueueSnackbar('Translation catalog field deleted', { variant: 'success' });
        },
        onError: (error) => handleMutationError('Delete field', error),
        onSettled: () => setDeletingFieldName(undefined),
      });
    },
    [deleteFieldMutation, enqueueSnackbar, handleMutationError, selectedCatalog],
  );

  const handleImportCsv = useCallback(
    async (file: File) => {
      try {
        const csvText = await file.text();
        const parsedCsv = parseTranslationCatalogCsv(csvText);
        setImportedDraft({ ...parsedCsv, nonce: Date.now() });
        enqueueSnackbar(`Loaded ${parsedCsv.rows.length} fields from CSV. Save to persist these changes.`, {
          variant: 'success',
        });

        if (parsedCsv.rejectedHeaders.length > 0) {
          enqueueSnackbar(`Ignored unsupported CSV columns: ${parsedCsv.rejectedHeaders.join(', ')}`, {
            variant: 'warning',
          });
        }
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
      }
    },
    [enqueueSnackbar],
  );

  const handleDownloadCsv = useCallback(() => {
    downloadCsvFile(
      buildTranslationCatalogCsvRows(editorRows, editorLocales),
      getTranslationCatalogCsvHeaders(editorLocales),
      getCsvFilename(selectedCatalog?.catalogName),
    );
    enqueueSnackbar('Translation catalog CSV downloaded', { variant: 'success' });
  }, [editorLocales, editorRows, enqueueSnackbar, selectedCatalog?.catalogName]);

  const handleTranslate = useCallback(() => {
    translateMutation.mutate(undefined, {
      onSuccess: () => {
        setIsDirty(false);
        enqueueSnackbar('Translation requested', { variant: 'success' });
      },
      onError: (error) => handleMutationError('Translate', error),
    });
  }, [enqueueSnackbar, handleMutationError, translateMutation]);

  const handleLock = useCallback(() => {
    lockMutation.mutate(undefined, {
      onSuccess: () => {
        setIsDirty(false);
        enqueueSnackbar('Translation catalog locked', { variant: 'success' });
      },
      onError: (error) => handleMutationError('Lock', error),
    });
  }, [enqueueSnackbar, handleMutationError, lockMutation]);

  const handleUpload = useCallback(() => {
    uploadMutation.mutate(undefined, {
      onSuccess: () => {
        enqueueSnackbar('Translation catalog uploaded to Braze', { variant: 'success' });
      },
      onError: (error) => handleMutationError('Upload to Braze', error),
    });
  }, [enqueueSnackbar, handleMutationError, uploadMutation]);

  const handleUnlock = useCallback(() => {
    unlockMutation.mutate(undefined, {
      onSuccess: () => {
        setIsUnlockDialogOpen(false);
        setIsDirty(false);
        enqueueSnackbar('Translation catalog unlocked', { variant: 'success' });
      },
      onError: (error) => handleMutationError('Unlock', error),
    });
  }, [enqueueSnackbar, handleMutationError, unlockMutation]);

  const handleSelectCatalog = useCallback(
    (catalogId: string) => {
      if (isDeletingCatalog || catalogId === selectedCatalogId) return;
      if (isDirty && !window.confirm('Discard unsaved changes and switch catalogs?')) return;

      setSelectedCatalogId(catalogId);
      setIsDirty(false);
      setImportedDraft(undefined);
    },
    [isDeletingCatalog, isDirty, selectedCatalogId],
  );

  const headerStats = useMemo(() => {
    const catalogs = catalogsQuery.data ?? [];
    const lockedCount = catalogs.filter(isTranslationCatalogLocked).length;

    return `${catalogs.length} catalogs · ${lockedCount} locked`;
  }, [catalogsQuery.data]);

  return (
    <Container maxWidth={false} sx={{ py: 3, maxWidth: 'min(100vw, 1900px)' }}>
      <Stack spacing={2.5}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
          <Stack spacing={0.5}>
            <Typography variant="h4" fontWeight={700}>
              Translation Catalog
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {headerStats}
            </Typography>
          </Stack>
          <TranslationCatalogToolbar
            canMutate={canMutate}
            isLocked={isLocked}
            isDirty={isDirty}
            isDeletingCatalog={isDeletingCatalog}
            isSubmitting={isSubmitting}
            hasCatalog={!!selectedCatalog}
            onSave={handleSave}
            onCreate={() => setIsNewDialogOpen(true)}
            onCreateFromCsv={handleCreateFromCsv}
            onDeleteCatalog={handleDeleteCatalog}
            onTranslate={handleTranslate}
            onUpload={handleUpload}
            onLock={handleLock}
            onUnlock={() => setIsUnlockDialogOpen(true)}
          />
        </Stack>

        {(catalogsQuery.isLoading || catalogQuery.isFetching) && <LinearProgress />}
        {catalogsQuery.error && <Alert severity="error">{getErrorMessage(catalogsQuery.error)}</Alert>}
        {catalogQuery.error && <Alert severity="error">{getErrorMessage(catalogQuery.error)}</Alert>}
        <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 1 }}>
          <Stack direction={{ xs: 'column', xl: 'row' }}>
            <TranslationCatalogList
              catalogs={catalogsQuery.data ?? []}
              selectedCatalogId={selectedCatalogId}
              isLoading={catalogsQuery.isLoading}
              onSelectCatalog={handleSelectCatalog}
            />
            <Box sx={{ minWidth: 0, flex: 1, px: 2, py: 1.5 }}>
              <TranslationCatalogEditor
                catalog={selectedCatalog}
                isLoading={catalogQuery.isFetching}
                isDeletingCatalog={isDeletingCatalog}
                deletingFieldName={isDeletingField ? deletingFieldName : undefined}
                canEdit={canMutate && !isSubmitting}
                canDeleteField={canMutate && !isLocked && !isDirty && !isSubmitting}
                canDownloadCsv={!!selectedCatalog && !isSubmitting}
                canLoadCsv={canMutate && !!selectedCatalog && !isLocked && !isDirty && !isSubmitting}
                isDirty={isDirty}
                importedDraft={importedDraft}
                onDownloadCsv={handleDownloadCsv}
                onImportCsv={handleImportCsv}
                onDeleteField={handleDeleteField}
                onRowsChange={handleRowsChange}
              />
            </Box>
          </Stack>
        </Paper>
      </Stack>

      <UnlockDialog
        open={isUnlockDialogOpen}
        catalogName={selectedCatalog?.catalogName}
        isSubmitting={unlockMutation.isPending}
        onClose={() => setIsUnlockDialogOpen(false)}
        onConfirm={handleUnlock}
      />
      <NewCatalogDialog
        open={isNewDialogOpen}
        isSubmitting={isCreating}
        onClose={() => setIsNewDialogOpen(false)}
        onCreate={handleCreate}
      />
    </Container>
  );
}

export default function TranslationCatalogPage() {
  return (
    <MUIProvider>
      <TranslationCatalogPageContent />
    </MUIProvider>
  );
}
